import React from 'react';
import type { IntlShape, MessageDescriptor } from 'react-intl-next';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl-next';

import type { FormatOptionLabelMeta, OptionProps, OptionType } from '@atlaskit/select';
import Select, { components } from '@atlaskit/select';

import type { CopySpacePermissionsUpsellProps } from '@confluence/change-edition/entry-points/copySpacePermissions';
import { CopySpacePermissionsUpsell } from '@confluence/change-edition/entry-points/copySpacePermissions';
import { fg } from '@confluence/feature-gating';

import { Option } from './Option';
import { OptionWithDescription } from './OptionWithDescription';
import { SelectLabel, customSelectStyles } from './LabeledSelect';

const OPTION_NOT_SELECTABLE_VALUE = '';

export type OptionSelectProps = {
	analyticsEventSourceName: CopySpacePermissionsUpsellProps['source'];
	options: Option[];
	defaultValue?: Option;
	label?: MessageDescriptor;
	onChange: (value: Option) => void;
	onMenuOpen?: () => void;
	showUpsell?: boolean;
	value?: Option;
	isDisabled?: boolean;
	fullWidth?: boolean;
	inputId?: string;
};

const formatOptionLabel = (
	option: OptionType,
	{ context, selectValue }: FormatOptionLabelMeta<OptionType>,
) => {
	if (context === 'menu') {
		const selected = selectValue?.[0]?.value;
		const isSelected = selected ? selected === option.value : false;
		return (
			<OptionWithDescription
				label={option.label}
				description={option.description}
				isSelected={isSelected}
				isMenu
			/>
		);
	} else if (fg('confluence_rbac_spaces_beta_opt_in')) {
		const selected = selectValue?.[0]?.value;
		const isSelected = selected ? selected === option.value : false;
		return (
			<OptionWithDescription
				label={option.label}
				description={option.description}
				isSelected={isSelected}
				isMenu={false}
			/>
		);
	} else {
		return option.label;
	}
};

const getOptionLabel = (option: OptionType) => {
	return `${option.label} ${option.description}`;
};

export const OptionComponent = (props: OptionProps<OptionType>) => {
	const { data, innerProps, innerRef } = props;
	return data.alternateComponent ? (
		<div ref={innerRef} {...innerProps}>
			{data.alternateComponent}
		</div>
	) : (
		<components.Option {...props} />
	);
};

export const OptionMessages = defineMessages({
	placeholder: {
		id: 'copy-space-permissions.selector.placeholder',
		defaultMessage: 'Select an option',
		description:
			"The placeholder of the dropdown inviting the user to select the 'Copy space permissions' option to apply to a target space",
	},
	defaultPermissions: {
		id: 'copy-space-permissions.selector.items.default',
		defaultMessage: 'Default permissions',
		description:
			'The dropdown option for when the user wants to use the default set of permissions for this space',
	},
	defaultPermissionsDescription: {
		id: 'copy-space-permissions.selector.items.default.description',
		defaultMessage: 'Use the space permissions set by your admin',
		description:
			'Description text for the dropdown option for when the user wants to use the default set of permissions for this space',
	},
	default: {
		id: 'copy-space-permissions.selector.items.default-roles',
		defaultMessage: 'Default',
		description:
			'The dropdown option for when the user wants to use the default roles for this space',
	},
	defaultDescription: {
		id: 'copy-space-permissions.selector.items.default-roles.description',
		defaultMessage: 'Use the access settings recommended by your admin',
		description:
			'The description for the dropdown option for when the user wants to use the default roles for this space',
	},
	onlyVisibleToMe: {
		id: 'copy-space-permissions.selector.items.private',
		defaultMessage: 'Only visible to me',
		description: 'The dropdown option for when the user wants to make this space private',
	},
	onlyVisibleToMeDescription: {
		id: 'copy-space-permissions.selector.items.private.description',
		defaultMessage: 'Only you can view this space',
		description:
			'Description text for the dropdown option for when the user wants to make this space private',
	},
	restricted: {
		id: 'copy-space-permissions.selector.items.restricted',
		defaultMessage: 'Restricted',
		description: 'The dropdown option for when the user wants to restrict access to this space',
	},
	restrictedDescription: {
		id: 'copy-space-permissions.selector.items.restricted.description',
		defaultMessage: 'Only you have access until you add others',
		description:
			'The description for the dropdown option for when the user wants to restrict access to this space',
	},
	copyPermissions: {
		id: 'copy-space-permissions.selector.items.copy.from.existing.space',
		defaultMessage: 'Copy permissions from another space',
		description:
			'The dropdown option for when the user wants to copy the permissions from an existing space into this space',
	},
	copyPermissionsDescription: {
		id: 'copy-space-permissions.selector.items.copy.from.existing.space.description',
		defaultMessage: 'Use the permissions from a similar space',
		description:
			'Description text for the dropdown option for when the user wants to copy the permissions from an existing space into this space',
	},
	copyAccess: {
		id: 'copy-space-permissions.selector.items.copy.access',
		defaultMessage: 'Copy access settings',
		description:
			'The dropdown option for when the user wants to copy the access settings from an existing space into this space',
	},
	copyAccessDescription: {
		id: 'copy-space-permissions.selector.items.copy.access.description',
		defaultMessage: 'Use another space’s settings',
		description:
			'The description for the dropdown option for when the user wants to copy the access settings from an existing space into this space',
	},
});

const copySpacePermissionsUpsellMessagesForNewSpace = defineMessages({
	title: {
		id: 'copy-space-permissions.for-new-space.upsell.title',
		defaultMessage: 'Setting up permissions can be faster than ever',
		description: 'Title for copy space permissions upsell when creating a new space',
	},
	body: {
		id: 'copy-space-permissions.for-new-space.upsell.description',
		defaultMessage: 'Quickly copy your unique permissions from another space with Premium.',
		description: 'Message to the user about copy space permissions',
	},
});

const OptionSelectComponent = ({
	analyticsEventSourceName,
	options,
	defaultValue,
	intl,
	label,
	onChange,
	onMenuOpen,
	showUpsell,
	value,
	isDisabled,
	fullWidth,
	inputId,
}: OptionSelectProps & { intl: IntlShape }) => {
	const optionList = [
		{
			label: intl.formatMessage(OptionMessages.onlyVisibleToMe),
			description: intl.formatMessage(OptionMessages.onlyVisibleToMeDescription),
			value: Option.ONLY_VISIBLE_TO_ME,
		},
		{
			label: intl.formatMessage(OptionMessages.defaultPermissions),
			description: intl.formatMessage(OptionMessages.defaultPermissionsDescription),
			value: Option.DEFAULT_PERMISSIONS,
		},
		{
			label: intl.formatMessage(OptionMessages.copyPermissions),
			description: intl.formatMessage(OptionMessages.copyPermissionsDescription),
			value: Option.COPY_PERMISSIONS_FROM_ANOTHER_SPACE,
		},
		{
			label: '',
			value: OPTION_NOT_SELECTABLE_VALUE,
			alternateComponent: (
				<CopySpacePermissionsUpsell
					source={analyticsEventSourceName}
					messages={copySpacePermissionsUpsellMessagesForNewSpace}
				/>
			),
			isDisabled: true,
		},
	];

	//TODO: Update the restricted options when RBAC is released
	const descriptiveOptionList = [
		{
			label: intl.formatMessage(OptionMessages.default),
			description: intl.formatMessage(OptionMessages.defaultDescription),
			value: Option.DEFAULT_PERMISSIONS,
		},
		{
			label: intl.formatMessage(OptionMessages.copyAccess),
			description: intl.formatMessage(OptionMessages.copyAccessDescription),
			value: Option.COPY_PERMISSIONS_FROM_ANOTHER_SPACE,
		},
		{
			label: intl.formatMessage(OptionMessages.restricted),
			description: intl.formatMessage(OptionMessages.restrictedDescription),
			value: Option.ONLY_VISIBLE_TO_ME,
		},
		{
			label: '',
			value: OPTION_NOT_SELECTABLE_VALUE,
			alternateComponent: (
				<CopySpacePermissionsUpsell
					source={analyticsEventSourceName}
					messages={copySpacePermissionsUpsellMessagesForNewSpace}
				/>
			),
			isDisabled: true,
		},
	];

	const availableOptions = fg('confluence_rbac_spaces_beta_opt_in')
		? descriptiveOptionList.filter(({ value }) =>
				// Only show the UpSell option (whose `value` is `OPTION_NOT_SELECTABLE_VALUE`) if UpSell is enabled
				value === OPTION_NOT_SELECTABLE_VALUE
					? showUpsell
					: options.indexOf(value as Option) !== -1,
			)
		: optionList.filter(({ value }) =>
				// Only show the UpSell option (whose `value` is `OPTION_NOT_SELECTABLE_VALUE`) if UpSell is enabled
				value === OPTION_NOT_SELECTABLE_VALUE
					? showUpsell
					: options.indexOf(value as Option) !== -1,
			);

	const onSelectMenuOpen = () => {
		if (typeof onMenuOpen === 'function') {
			onMenuOpen();
		}
	};

	const onSelectChange = (selection) => {
		if (typeof onChange === 'function') {
			onChange(selection.value);
		}
	};

	const select = (
		<Select
			classNamePrefix="option-select"
			components={{ Option: OptionComponent }}
			defaultValue={availableOptions.find((option) => option.value === defaultValue)}
			formatOptionLabel={formatOptionLabel}
			getOptionLabel={getOptionLabel}
			id="copy-space-perms-option-select"
			isDisabled={isDisabled || availableOptions.length === 1}
			maxMenuHeight={350}
			onChange={onSelectChange}
			onMenuOpen={onSelectMenuOpen}
			options={availableOptions}
			placeholder={intl.formatMessage(OptionMessages.placeholder)}
			// @ts-ignore this prop doesn't seem to be defined
			// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			styles={customSelectStyles(fullWidth)}
			value={availableOptions.find((option) => option.value === value)}
			inputId={inputId}
			isSearchable={false}
		/>
	);

	const optionSelect = (
		<Select
			classNamePrefix="option-select"
			components={{ Option: OptionComponent }}
			defaultValue={availableOptions.find((option) => option.value === defaultValue)}
			formatOptionLabel={formatOptionLabel}
			getOptionLabel={getOptionLabel}
			id="copy-space-perms-option-select"
			isDisabled={isDisabled || availableOptions.length === 1}
			maxMenuHeight={350}
			onChange={onSelectChange}
			onMenuOpen={onSelectMenuOpen}
			options={availableOptions}
			placeholder={intl.formatMessage(OptionMessages.placeholder)}
			// @ts-ignore this prop doesn't seem to be defined
			// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			styles={customSelectStyles(fullWidth)}
			value={availableOptions.find((option) => option.value === value)}
			inputId={inputId}
			isSearchable={false}
		/>
	);

	const selectComponent = fg('confluence_rbac_spaces_beta_opt_in') ? optionSelect : select;

	const selectComponentWithLabel = (
		<div>
			<SelectLabel htmlFor="copy-space-perms-option-select">
				<FormattedMessage {...label} />
			</SelectLabel>
			{selectComponent}
		</div>
	);

	return label ? selectComponentWithLabel : selectComponent;
};

export const OptionSelect = injectIntl(OptionSelectComponent);
